import { gql } from '@apollo/client';
import inflection from 'inflection';
import { Delta } from 'quill/core';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from '../utils/router/v3Bridge';
import AvatarStack from '../components/AvatarStack';
import Card, { CardSeparator } from '../components/Card';
import CardHeader from '../components/CardHeader';
import DeltaPreview from '../components/DeltaPreview';
import EntityPill from '../components/EntityPill';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import WrappingTwoColumnLayout from '../components/WrappingTwoColumnLayout';
import type {
  PostResultCard_AncestorsFragment,
  PostResultCard_PostFragment,
} from '../graphql/generated/graphqlTypes';
import clockIcon from '../icons/clock.svg';
import PostBadge from '../post/PostBadge';
import PostDetailPreviewPopOver from '../post/PostDetailPreviewPopOver';
import PostIcon from '../post/PostIcon';
import PostTitleWithIcons from '../post/PostTitleWithIcons';
import PostSeriesMeta from '../post/series/PostSeriesMeta';
import TopicPills from '../topic/TopicPills';
import isSize from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import renderHighlight from '../utils/search/renderHighlight';
import postPath from '../utils/url/postPath';
import styles from './PostResultCard.module.scss';
import { saveSearchContext } from './searchContext';
import imageIcon from '../icons/image.svg';

const usePostPopOverOffset = () => {
  const [headerWidth, setHeaderWidth] = useState<number>(0);
  const headerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (headerRef.current) {
      const setWidth = () =>
        setHeaderWidth(headerRef?.current?.getBoundingClientRect().width ?? 0);
      const resizeObserver = new ResizeObserver(setWidth);
      resizeObserver.observe(headerRef.current);
      setWidth();

      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, []);

  const offset = useMemo<[number, number]>(
    () => [0, headerWidth / 2 - 32], // 32px - icon width
    [headerWidth],
  );

  return [headerRef, offset] as const;
};

const POST_FRAGMENT = gql`
  fragment PostResultCard_post on Post {
    id
    views
    thanks
    updatedAt
    publishedAt
    userViewedAt
    series {
      ...PostSeriesMeta_series
    }
    ...postPath_post
    ...PostBadge_post
    ...PostIcon_post
    ...PostTitleWithIcons_post
    topics {
      ...TopicPills_topics
    }
    contributors {
      ...AvatarStack_users
    }
  }
  ${postPath.fragments.post}
  ${TopicPills.fragments.topics}
  ${AvatarStack.fragments.users}
  ${PostBadge.fragments.post}
  ${PostSeriesMeta.fragments.series}
  ${PostIcon.fragments.post}
  ${PostTitleWithIcons.fragments.post}
`;

const ANCESTORS_FRAGMENT = gql`
  fragment PostResultCard_ancestors on AncestorSearchResult {
    id
    name
  }
`;

interface PostResultCardProps {
  post: PostResultCard_PostFragment;
  title?: string | null;
  content: Delta;
  queryId?: string;
  position: number;
  highlight: Delta;
  highlightIndex?: number;
  ancestors?: PostResultCard_AncestorsFragment[];
}

const PostResultCard = ({
  post,
  content,
  title,
  queryId,
  position,
  highlight,
  highlightIndex,
  ancestors = [],
}: PostResultCardProps) => {
  const path = postPath(post);

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const showViewedAtMetaOnBottom = isSize(Sizes.SM);

  const viewedAtMeta = post.userViewedAt && (
    <div className={styles.viewedMeta}>
      <SVGIcon inline size="small" className={styles.viewedAtIcon}>
        {clockIcon}
      </SVGIcon>{' '}
      You viewed <Timestamp date={post.userViewedAt} />
    </div>
  );
  const [headerRef, offset] = usePostPopOverOffset();

  const contributors = post.contributors || [];

  const hasBadge = post.archivedAt || post.publishedAt === null;
  const hasRight = contributors.length > 0 || hasBadge;
  const hasFooter = (post.topics ?? []).length > 0 || hasRight;

  const handleClick = () => {
    const searchHighlightOpIndex = highlight.ops.findIndex(
      (op) => op.attributes?.searchHighlight,
    );
    const highlightedRangeIndex =
      highlightIndex && searchHighlightOpIndex !== -1
        ? highlightIndex +
          new Delta(highlight.ops.slice(0, searchHighlightOpIndex)).length()
        : undefined;

    saveSearchContext(post.id, { position, queryId, highlightedRangeIndex });
  };

  return (
    <Card to={path} onClick={handleClick}>
      <div ref={headerRef}>
        <CardHeader
          icon={
            <PostDetailPreviewPopOver
              postId={post.id}
              offset={offset}
              queryId={queryId}
              position={position}
            >
              <div className={styles.icon}>
                <PostIcon post={post} />
                <Link to={path} className={styles.hoverTarget} />
              </div>
            </PostDetailPreviewPopOver>
          }
          aboveTitle={
            post.series && (
              <div className={styles.seriesContainer}>
                <PostSeriesMeta series={post.series} postId={post.id} />
              </div>
            )
          }
          title={
            <PostTitleWithIcons
              post={post}
              title={title && renderHighlight(title)}
            />
          }
          meta={
            'publishedAt' in post && (
              <>
                <ul className={styles.metaList}>
                  {post.updatedAt ? (
                    <li>
                      Updated <Timestamp date={post.updatedAt} />
                    </li>
                  ) : null}
                  {post.views > 0 && (
                    <li>
                      {post.views} {inflection.inflect('view', post.views)}
                    </li>
                  )}
                  {post.thanks > 0 && (
                    <li>
                      {post.thanks} {inflection.inflect('thanks', post.thanks)}
                    </li>
                  )}
                </ul>
                {showViewedAtMetaOnBottom && (
                  <div className={styles.bottomMeta}>{viewedAtMeta}</div>
                )}
              </>
            )
          }
          rightMeta={!showViewedAtMetaOnBottom && viewedAtMeta}
        />
      </div>
      <CardSeparator />
      <DeltaPreview
        typography="searchResult"
        content={content}
        maxLines={8}
        link={false}
        additionalBlockEmbedHandlers={{
          // eslint-disable-next-line react/no-unstable-nested-components
          image: (value, key, attributes) => {
            if (typeof value === 'string' || Array.isArray(value)) {
              const { caption } = attributes;
              const searchHighlight =
                typeof attributes.searchHighlight === 'string'
                  ? renderHighlight(attributes.searchHighlight)
                  : null;

              return (
                <div key={key}>
                  <EntityPill
                    icon={<SVGIcon size={12}>{imageIcon}</SVGIcon>}
                    type="searchResultEmbed"
                  >
                    <span>
                      {searchHighlight ?? caption ?? 'Uploaded Image'}
                    </span>
                  </EntityPill>
                </div>
              );
            }
            return null;
          },
        }}
      />
      {hasFooter && (
        <div className={styles.footer}>
          <WrappingTwoColumnLayout
            left={
              <TopicPills
                topics={post.topics}
                names={post.topics.map(
                  (topic) => ancestors.find((a) => a.id === topic.id)?.name,
                )}
                highlighted
              />
            }
            right={
              hasRight && (
                <div className={styles.right}>
                  <AvatarStack
                    link
                    showHoverCard
                    users={contributors.slice(0, 3)}
                  />
                  <PostBadge post={post} inline={false} />
                </div>
              )
            }
          />
        </div>
      )}
    </Card>
  );
};

PostResultCard.fragments = {
  post: POST_FRAGMENT,
  ancestors: ANCESTORS_FRAGMENT,
};

export default PostResultCard;
